import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  useCreateComponentMutation,
  type Child,
  type ChildInput,
  type Document,
  type Reference,
  type ReferenceInput,
} from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { payloadId, shown } from '../api/useIntent'
import { placementTarget } from '../model/command'
import type { CompositionNode } from '../model/composition'

/** Create an inline placement's own component, then relink the placement to it. */
export function ExtractDialog({ tenant, document, node, title, onClose, send }: {
  tenant: string
  document: Document
  node: CompositionNode | null
  title: string
  onClose: () => void
  send: (id: string, intent: string, payload: Record<string, unknown>, path?: string[]) => Promise<unknown>
}) {
  const [create, creation] = useCreateComponentMutation()
  const [created, setCreated] = useState<Document | null>(null)
  const [promoteError, setPromoteError] = useState<unknown>()
  const child = node ? childAt(document.children, node.path) : undefined
  const holder = node ? placementTarget(document, node.path) : undefined

  useEffect(() => {
    if (!node) return
    setCreated(null)
    setPromoteError(undefined)
    creation.reset()
  }, [node, creation.reset])

  const promote = async (target: Document) => {
    if (!child || !holder || holder.crossedLink) return
    try {
      setPromoteError(undefined)
      await send(holder.id, 'component.child_promoted', {
        linkId: child.linkId,
        id: payloadId(tenant, target.id),
      }, holder.path)
      onClose()
    } catch (error) {
      setPromoteError(error)
    }
  }

  const extract = async () => {
    if (!child) return
    const target = await create({
      tenant,
      createComponent: {
        type: child.type,
        fields: child.fields,
        children: child.children?.map(creationChild),
        references: child.references?.map(creationReference),
      },
    }).unwrap()
    setCreated(target)
    await promote(target)
  }

  const busy = creation.isLoading
  return (
    <Dialog open={Boolean(node)} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Make this {title.toLowerCase()} reusable?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            Its content will move into a component of its own. This placement will follow that component&rsquo;s
            latest revision, and it can then be reused elsewhere or pinned.
          </Typography>
          <Alert severity="info" icon={false}>
            Extraction uses two changes: create, then relink. If relinking fails, the new component is kept and this
            dialog offers the relink again.
          </Alert>
          {creation.error && (
            <Alert severity="error">{problemMessage(creation.error, 'The component was not created.')}</Alert>
          )}
          {Boolean(promoteError) && created && (
            <Alert severity="error">
              {problemMessage(
                promoteError,
                `The component ${created.id} was created, but this placement was not relinked.`,
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {created && promoteError ? (
          <Button variant="contained" disabled={busy} onClick={() => promote(created).catch(shown)}>Relink</Button>
        ) : (
          <Button variant="contained" disabled={busy || !child || !holder} onClick={() => extract().catch(shown)}>Extract</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

function childAt(children: Child[] | undefined, path: string[]): Child | undefined {
  let level = children
  let found: Child | undefined
  for (const [index, linkId] of path.entries()) {
    found = level?.find((child) => child.linkId === linkId)
    if (!found || found.id) return found && index === path.length - 1 ? found : undefined
    level = found.children
  }
  return found
}

/** Convert a read child back to create input, dropping resolved types from links. */
function creationChild(child: Child): ChildInput {
  if (child.id) {
    return { linkId: child.linkId, id: child.id, revision: child.revision }
  }
  return {
    linkId: child.linkId,
    type: child.type,
    fields: child.fields,
    children: child.children?.map(creationChild),
    references: child.references?.map(creationReference),
  }
}

/** A reference target's type is the platform's to resolve on creation. */
function creationReference(reference: Reference): ReferenceInput {
  return {
    edgeId: reference.edgeId,
    name: reference.name,
    id: reference.id,
    revision: reference.revision,
  }
}
